import React, { useState, useEffect } from 'react';
import api from './axiosConfig';
import './ForecastModals.css';

const EndOfDayPerformanceReview = ({ onClose }) => {
  const [weekData, setWeekData] = useState(null);
  const [selectedDay, setSelectedDay] = useState('latest');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWeekData();
  }, []);

  const fetchWeekData = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/forecast/current-week');
      
      if (!response.data || !response.data.has_data) {
        setError('No forecast data available for this week.');
        setLoading(false);
        return;
      }

      const weekStart = new Date(response.data.week_start_date);
      const dailyBreakdown = response.data.daily_breakdown || [];
      
      const transformedData = {
        week_start: response.data.week_start_date,
        days: dailyBreakdown.map(day => {
          const performance = day.forecast_revenue > 0 
            ? (day.actual_revenue / day.forecast_revenue) * 100 
            : 0;
          
          const status = day.actual_revenue > 0 ? 'completed' : 'pending';
          
          const dayDate = new Date(weekStart);
          dayDate.setDate(weekStart.getDate() + (day.day_of_week - 1));
          
          return {
            date: dayDate.toISOString().split('T')[0],
            day_name: day.day_name,
            day_of_week: day.day_of_week,
            status: status,
            total_forecast: day.forecast_revenue || 0,
            total_actual: day.actual_revenue || 0,
            performance: performance,
            staff: day.staff_details || []
          };
        })
      };

      setWeekData(transformedData);
      
      const latestCompleted = transformedData.days.filter(d => d.status === 'completed').pop();
      if (latestCompleted) {
        setSelectedDay(latestCompleted.date);
      }

    } catch (error) {
      console.error('Error fetching week data:', error);
      setError('Failed to load performance data. ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getCompletedDays = () => {
    return weekData?.days.filter(day => day.status === 'completed') || [];
  };

  const getSelectedDayData = () => {
    if (selectedDay === 'latest') {
      return getCompletedDays().pop();
    }
    return weekData?.days.find(day => day.date === selectedDay);
  };

  const getPerformanceColor = (percentage) => {
    if (percentage >= 100) return '#059669';
    if (percentage >= 90) return '#d97706';
    return '#dc2626';
  };

  const getPerformanceIcon = (percentage) => {
    if (percentage >= 100) return '🎯';
    if (percentage >= 90) return '⚡';
    return '📈';
  };

  const getWeekSummary = () => {
    const completed = getCompletedDays();
    const totalForecast = completed.reduce((sum, day) => sum + day.total_forecast, 0);
    const totalActual = completed.reduce((sum, day) => sum + day.total_actual, 0);
    const avgPerformance = completed.length > 0 ? totalActual / totalForecast * 100 : 0;
    
    return { totalForecast, totalActual, avgPerformance, daysCompleted: completed.length };
  };

  if (loading) {
    return (
      <div className="forecast-modal-overlay">
        <div className="forecast-modal-container" style={{ maxWidth: '400px' }}>
          <div className="forecast-loading">
            <div className="forecast-spinner"></div>
            <span style={{ marginLeft: '12px', color: '#6b7280' }}>Loading performance data...</span>
          </div>
        </div>
      </div>
    );
  }

  const selectedDayData = getSelectedDayData();
  const weekSummary = getWeekSummary();

  return (
    <div className="forecast-modal-overlay">
      <div className="forecast-modal-container" style={{ maxWidth: '1000px' }}>
        <div className="forecast-modal-header">
          <div className="forecast-modal-title">
            <div className="forecast-modal-icon indigo">
              <svg width="24" height="24" fill="none" stroke="#6366f1" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h2 className="forecast-modal-h2">Performance Review</h2>
              <p className="forecast-modal-subtitle">Review completed days when income cards are submitted</p>
            </div>
          </div>
          <button onClick={onClose} className="forecast-modal-close">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="forecast-modal-content">
          {error && (
            <div className="forecast-alert forecast-alert-error">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {!error && weekData && (
            <>
              <div className="forecast-grid forecast-grid-4" style={{ marginBottom: '24px' }}>
                <div className="forecast-summary-card blue">
                  <div className="forecast-summary-value blue">{weekSummary.daysCompleted}</div>
                  <div className="forecast-summary-label">Days Completed</div>
                </div>
                <div className="forecast-summary-card purple">
                  <div className="forecast-summary-value purple">£{weekSummary.totalForecast.toFixed(0)}</div>
                  <div className="forecast-summary-label">Week Forecast</div>
                </div>
                <div className="forecast-summary-card green">
                  <div className="forecast-summary-value green">£{weekSummary.totalActual.toFixed(0)}</div>
                  <div className="forecast-summary-label">Week Actual</div>
                </div>
                <div className="forecast-summary-card" style={{ backgroundColor: '#f0f4ff', borderColor: '#c7d2fe' }}>
                  <div className="forecast-summary-value" style={{ color: '#4338ca' }}>
                    {weekSummary.avgPerformance.toFixed(1)}%
                  </div>
                  <div className="forecast-summary-label">Week Performance</div>
                </div>
              </div>

              <div className="forecast-form-group" style={{ marginBottom: '24px' }}>
                <label className="forecast-form-label">Select Day to Review</label>
                <select
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(e.target.value)}
                  className="forecast-form-select"
                  style={{ maxWidth: '400px' }}
                >
                  <option value="latest">Latest Completed Day</option>
                  {getCompletedDays().map(day => (
                    <option key={day.date} value={day.date}>
                      {day.day_name} ({new Date(day.date).toLocaleDateString()}) - {day.performance.toFixed(1)}%
                    </option>
                  ))}
                </select>
              </div>

              {selectedDayData && (
                <div style={{ marginBottom: '24px' }}>
                  <div 
                    className="forecast-perf-card" 
                    style={{ 
                      borderColor: getPerformanceColor(selectedDayData.performance),
                      backgroundColor: selectedDayData.performance >= 100 ? '#f0fdf4' : 
                                       selectedDayData.performance >= 90 ? '#fffbeb' : '#fef2f2'
                    }}
                  >
                    <div className="forecast-perf-header">
                      <div>
                        <h3 className="forecast-perf-title">{selectedDayData.day_name}</h3>
                        <p className="forecast-perf-date">
                          {new Date(selectedDayData.date).toLocaleDateString('en-GB', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div className="forecast-perf-emoji">{getPerformanceIcon(selectedDayData.performance)}</div>
                        <div className="forecast-perf-percentage" style={{ color: getPerformanceColor(selectedDayData.performance) }}>
                          {selectedDayData.performance.toFixed(1)}%
                        </div>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>Performance</div>
                      </div>
                    </div>
                    
                    <div className="forecast-perf-stats">
                      <div>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>Revenue</div>
                        <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>
                          £{selectedDayData.total_actual.toFixed(2)} / £{selectedDayData.total_forecast.toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>Status</div>
                        <div style={{ fontSize: '1rem', fontWeight: '500' }}>
                          {selectedDayData.status === 'completed' ? '✅ Income Card Completed' : '⏳ Pending'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '24px' }}>
                    <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '16px' }}>Staff Performance</h4>
                    {selectedDayData.staff && selectedDayData.staff.length > 0 ? (
                      selectedDayData.staff.map(staff => (
                        <div key={staff.staff_id} className="forecast-perf-card" style={{ backgroundColor: '#f9fafb', marginBottom: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                            <div className="forecast-staff-avatar">
                              <svg width="20" height="20" fill="#8b5cf6" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <div>
                              <h5 style={{ fontWeight: '500', color: '#1f2937', margin: 0 }}>{staff.staff_name}</h5>
                              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                                Performance: {staff.forecast_revenue > 0 ? ((staff.actual_revenue / staff.forecast_revenue) * 100).toFixed(1) : 0}%
                              </p>
                            </div>
                          </div>

                          <div className="forecast-perf-stats">
                            <div>
                              <div style={{ fontSize: '14px', color: '#6b7280' }}>Revenue</div>
                              <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>
                                £{staff.actual_revenue.toFixed(2)} / £{staff.forecast_revenue.toFixed(2)}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: '14px', color: '#6b7280' }}>Appointments</div>
                              <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>
                                {staff.actual_appointments} / {staff.forecast_appointments}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ textAlign: 'center', padding: '16px', color: '#6b7280' }}>
                        No staff data available for this day
                      </div>
                    )}
                  </div>
                </div>
              )}

              {getCompletedDays().length === 0 && (
                <div style={{ textAlign: 'center', padding: '32px' }}>
                  <div style={{ color: '#9ca3af', marginBottom: '16px' }}>
                    <svg width="64" height="64" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ margin: '0 auto' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1f2937', marginBottom: '8px' }}>No Completed Days Yet</h3>
                  <p style={{ color: '#6b7280' }}>Performance data will appear here after income cards are completed each day.</p>
                </div>
              )}

              <div className="forecast-alert forecast-alert-info">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 style={{ fontWeight: '500', marginBottom: '8px' }}>How Performance Review Works</h3>
                  <ul style={{ margin: '0', paddingLeft: '16px' }}>
                    <li style={{ fontSize: '14px', marginBottom: '4px' }}>Performance data appears automatically after income cards are completed</li>
                    <li style={{ fontSize: '14px', marginBottom: '4px' }}>Compare daily actuals against your weekly forecasts</li>
                    <li style={{ fontSize: '14px', marginBottom: '4px' }}>Review individual staff performance and appointment counts</li>
                    <li style={{ fontSize: '14px', marginBottom: '4px' }}>Use insights to adjust future forecasts and improve planning</li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="forecast-modal-footer">
          <div className="forecast-footer-info">
            Data updates automatically when income cards are submitted
          </div>
          <button onClick={onClose} className="forecast-btn forecast-btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EndOfDayPerformanceReview;